/**
 * BL-1912 — the Zod shape of a `MoneyWire` input (ADR 060 — Money Type § 2.3):
 * an exact decimal string that always travels with its currency. Shared by any
 * form or route that takes an amount, so the rules — never below the minimum,
 * never past the money ceiling of the amount's own currency (BUG-1903), never
 * more decimals than the column holds — live in one place. Client-bundle-safe.
 */
import Decimal from 'decimal.js';

import { z } from '../zod';
import { exceedsMoneyCeiling, format, isSupportedCurrency, Money, moneyCeiling } from '../isomorphic/money';
import { decodeWireAmount } from '../mappers/realtime/money-wire-amount';

/**
 * The field message for an amount that is not a decimal string (or not a
 * MoneyWire at all — a bare number from a machine caller). One source, so every
 * MoneyWire field refuses malformed input in the same words (BL-2117).
 */
export const MONEY_WIRE_MALFORMED_MESSAGE = 'Enter an amount like 1234.50';

/** A plain decimal: optional minus, digits, optional fraction. No exponent, no commas. */
const DECIMAL_RE = /^-?\d+(\.\d+)?$/;

/** Options for {@link moneyWireSchema}. */
export interface MoneyWireSchemaOptions {
  /** Most digits allowed after the point — the storage column's scale. */
  maxScale: number;
  /** Smallest amount allowed. Defaults to 0 (`0` itself is a real price). */
  min?: number;
  /**
   * BL-2019 — refuse zero as well as negatives. A bid is never `0`; a price
   * (the BL-1912 lot inputs) can be, which is why this is opt-in.
   */
  positive?: boolean;
  /**
   * BL-2020 — the field's own copy for a zero / negative amount, when the form
   * already promised the operator one (the Sell to buyer form's BL-1095
   * "Sale price must be greater than zero."). Defaults to the generic message.
   * BL-2374: a function receives the amount's currency, so the copy can name
   * its zero ("Enter an amount above $0.00.").
   */
  positiveMessage?: PositiveMessage;
  /**
   * BL-2374 — read a blank amount as zero, so an empty box shows the
   * positive-amount copy rather than the malformed-amount one. Only with
   * `positive`. Defaults to false: a blank is malformed.
   */
  blankAsZero?: boolean;
}

/** A zero / negative amount's copy: fixed, or worded from the amount's currency. */
export type PositiveMessage = string | ((currency: string) => string);

/**
 * Build the Zod schema for one `MoneyWire` input. Every amount issue is
 * reported on the object itself (path `[]`), so a form shows it under the
 * field that holds the price rather than under a hidden `amount` key.
 *
 * @param opts See {@link MoneyWireSchemaOptions}.
 * @returns A schema that parses `{ amount, currency }` unchanged.
 * @sideEffect none — pure.
 */
export function moneyWireSchema(opts: MoneyWireSchemaOptions) {
  const min = opts.min ?? 0;
  return z
    .object({ amount: z.string(), currency: z.string() })
    .superRefine((wire, ctx) => {
      const message = amountIssue({
        amount: wire.amount,
        currency: wire.currency,
        min,
        maxScale: opts.maxScale,
        positive: opts.positive ?? false,
        positiveMessage: opts.positiveMessage,
        blankAsZero: opts.blankAsZero ?? false,
      });
      if (message) ctx.addIssue({ code: 'custom', path: [], message });
      if (!isSupportedCurrency(wire.currency)) ctx.addIssue({ code: 'custom', path: [], message: 'Unsupported currency' });
    });
}

/**
 * The first thing wrong with an amount string, or null when it is valid.
 *
 * @returns The field message, or null.
 * @sideEffect none — pure.
 */
function amountIssue(input: {
  amount: string;
  currency: string;
  min: number;
  maxScale: number;
  positive: boolean;
  positiveMessage?: PositiveMessage;
  blankAsZero: boolean;
}): string | null {
  const { currency, min, maxScale, positive, positiveMessage } = input;
  const amount = input.blankAsZero && positive && input.amount.trim() === '' ? '0' : input.amount;
  if (!DECIMAL_RE.test(amount)) return MONEY_WIRE_MALFORMED_MESSAGE;
  const value = new Decimal(amount);
  if (positive && value.lessThanOrEqualTo(0)) {
    if (positiveMessage == null) return 'Enter an amount greater than 0';
    return typeof positiveMessage === 'string' ? positiveMessage : positiveMessage(currency);
  }
  if (value.lessThan(min)) return `Enter an amount of ${min} or more`;
  if (isSupportedCurrency(currency) && exceedsMoneyCeiling(Money.of(value, currency))) {
    // BUG-1903: the ceiling and its copy are in the amount's own currency (¥9,999,999,999,
    // $9,999,999,999.99). An unsupported currency has its own refusal below.
    return `Enter an amount of at most ${format(moneyCeiling(currency))}`;
  }
  if (value.decimalPlaces() > maxScale) return `Use at most ${maxScale} decimal places`;
  return null;
}

/** Storage scale of every `numeric(18,4)` money column (ADR 060 — Money Type § 2.1). */
const STORAGE_SCALE = 4;

/**
 * BL-2019 — the one request-side decoder for a MoneyWire amount: the
 * {@link moneyWireSchema} rules, then the shared `decodeWireAmount` (BL-2061), so a route's
 * `parseBody` hands its service a `Money` and nothing on the way decodes the
 * amount twice, by arithmetic, or through `Number()`. The scale cap is the
 * column's (4), not the currency's minor unit: an amount finer than the
 * currency (¥1,000.5) is the bid engine's to refuse as off the ladder, with its
 * own copy and nearest amounts, not a schema error.
 *
 * @param opts - `positive` refuses zero too (every bid amount); defaults to true.
 *   `positiveMessage` replaces the generic zero / negative copy (BL-2020), or
 *   words it from the currency (BL-2374). `blankAsZero` reads an empty box as zero.
 * @returns A Zod schema that parses `{ amount, currency }` into a `Money`.
 * @sideEffect none — pure.
 */
export function moneyWireAmountField(opts: { positive?: boolean; positiveMessage?: PositiveMessage; blankAsZero?: boolean } = {}) {
  return moneyWireSchema({
    maxScale: STORAGE_SCALE,
    positive: opts.positive ?? true,
    positiveMessage: opts.positiveMessage,
    blankAsZero: opts.blankAsZero,
  }).transform((wire, ctx) => {
    // The one MoneyWire → Money decode the payloads use too (BL-2061). The
    // refinements above already guarantee a decodable wire; the branch keeps
    // the decoder total rather than asserting.
    const decoded = decodeWireAmount(wire);
    if (decoded.kind === 'money') return decoded.money;
    ctx.addIssue({ code: 'custom', path: [], message: MONEY_WIRE_MALFORMED_MESSAGE });
    return z.NEVER;
  });
}
